import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { IQueryProps } from '../interfaces/general/api';
import { IRotation } from '@clickster/constant-definitions/dist-esm/entities/rotation/types';
import { RotationsService } from '../services/rotations';
import flattenDeep from 'lodash/flattenDeep';
import { IReport } from '@clickster/business-logic/dist-esm/reports/types/analytics';

interface IGetRotations extends IQueryProps<IRotation> {
  queryName: string;
}

export const useGetRotations = ({ params, queryName, handleSuccess, handleError, enabled }: IGetRotations) => {
  const {
    data = [],
    isLoading,
    isFetching,
    refetch,
  } = useQuery([queryName], () => RotationsService.list(params), {
    onError: (error) => {
      console.log({ error });
    },
    onSuccess: handleSuccess ? handleSuccess : undefined,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
    enabled
  });

  return {
    rotations: data as IRotation[],
    isLoading,
    isFetching,
    refetch,
  };
};

export const useFindRotationById = (
  id: string,
  data?: any,
  params?: {},
  handleSuccess?: (data: IRotation) => void,
  handleError?: (error: any) => void,
  enabled?: boolean,
) => {
  const {
    data: responseData,
    isLoading,
    isFetching,
    refetch,
  } = useQuery(
    ['rotation', id, data?.options?.datasource], // Include datasource in query key to refetch when it changes
    () => RotationsService.findById({ id, data, params }),
    {
      onError: (error) => {
        console.log({ error });
        if (handleError) handleError(error);
      },
      onSuccess: handleSuccess ? handleSuccess : undefined,
      retry: false,
      refetchOnWindowFocus: false,
      enabled,
    },
  );

  return {
    rotation: responseData as IRotation | undefined,
    isLoading,
    isFetching,
    refetch,
  };
};

export const useUpdateRotationById = () => {
  const { mutateAsync, isError, isLoading, isSuccess } = useMutation(
    (data: IRotation) => RotationsService.update(data),
    {
      onError: (error) => console.log({ error }),
    },
  );

  return {
    updateRotationById: mutateAsync,
    isError,
    isLoading,
    isSuccess,
  };
};

export const useGetRotationCountries = ({ handleSuccess }: IQueryProps<IRotation>) => {
  const {
    data = [],
    isLoading,
    isFetching,
    refetch,
  } = useQuery(['countries'], () => RotationsService.countries(), {
    onError: (error) => {
      console.log({ error });
    },
    onSuccess: handleSuccess ? handleSuccess : undefined,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  });

  return {
    countries: data as any[],
    isLoading,
    isFetching,
    refetch,
  };
};

interface IGetRotationsByCountry extends IQueryProps<IRotation> {
  queryName: string;
  countryCode: string;
}

export const useGetRotationRegionsByCountry = ({ countryCode, queryName, enabled }: IGetRotationsByCountry) => {
  const {
    data = [],
    isLoading,
    isFetching,
    refetch,
  } = useQuery(['regionsbyCountry', countryCode], () => RotationsService.regionsByCountry(countryCode), {
    onError: (error) => {
      console.log({ error });
    },
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
    enabled,
  });

  return {
    regions: data,
    isLoading,
    isFetching,
    refetch,
  };
};

interface IUseCitiesByCountryQueries extends IQueryProps<IRotation> {
  countries: { countryName: string; regionName: string }[];
}

export const useCitiesByCountryQueries = ({ countries, customKey, enabled }: IUseCitiesByCountryQueries) => {
  const queries = countries.map(({ countryName, regionName }) => {
    return {
      queryKey: ['rotation-cities', countryName, regionName],
      queryFn: () => RotationsService.citiesByRegion(countryName, regionName),
      retry: false,
      refetchOnWindowFocus: false,
      enabled,
    };
  });

  const citiesQueries = useQueries({ queries });

  const isLoading = citiesQueries.some((item) => item.isLoading);

  const data = citiesQueries?.map((item) => item.data);

  const dataByCountry = (data ?? [])?.map((item = [], index) => {
    return item?.map((city) => `${city} - ${countries[index]?.regionName}`).sort();
  });

  return {
    data: dataByCountry?.length > 0 ? (flattenDeep(dataByCountry) as string[]) : ([] as string[]),
    isLoading,
  };
};


export const useCreateRotation = () => {
  const { mutateAsync, isError, isLoading, isSuccess } = useMutation(
    (data: IRotation) => RotationsService.create(data),
    {
      onError: (error) => console.log({ error }),
    },
  );

  return {
    mutateAsync,
    isError,
    isLoading,
    isSuccess,
  };
};

interface IUseGetRotationById extends IQueryProps<IRotation> {
  rotationId: string;
}

export const useGetRotationById = ({ rotationId, enabled, staleTime, customKey = 'useGetRotationById' }: IUseGetRotationById) => {
  const { data: rotationData, isLoading, isFetching, refetch } = useQuery(
    [customKey, rotationId],
    () => RotationsService.getById(rotationId!, { select: 'name' }),
    {
      enabled,
      staleTime,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  return {
    rotation: rotationData,
    isLoading,
    isFetching,
    refetch,
  }
}


interface IUseGetRotationAnalyticsById extends IQueryProps<IRotation> {
  rotationId: string;
  report: Pick<IReport, 'filters' | 'dates'>;
}


export const useGetRotationAnalyticsById = ({ rotationId, enabled, staleTime, customKey = 'useGetRotationAnalyticsById', report }: IUseGetRotationAnalyticsById) => {
  const { data: rotationData, isLoading, isFetching, refetch } = useQuery(
    [customKey, rotationId],
    () => RotationsService.getRotationAnalyticsById({ rotationId, report }),
    {
      enabled,
      staleTime,
      retry: 5,
      refetchOnWindowFocus: false,
    },
  );

  return {
    rotation: rotationData,
    isLoading,
    isFetching,
    refetch,
  }
}